import treeImage from '../../../assets/images/auth/loginTree.png';
import frogImage from '../../../assets/images/auth/loginFrog.png';

import LoginForm from '../components/login-form';
import classes from './login-page.module.css';
import { ActionFunction, redirect } from 'react-router-dom';
import { devbackendUrl } from '../../../utils/constants';
import { analyticsApi } from '../../../utils/analytics';
import { jwtDecode } from 'jwt-decode';
import {
  clearAllGameContexts,
  readActiveGameContext,
  readHopGameData,
} from '../../../utils/active-game-context';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { getRedirectToFromRequest } from '../../../utils/auth-redirect';
import {
  createAuthActionError,
  getNetworkAuthError,
  getResponseErrorMessage,
} from '../utils/auth-action-errors';

const LoginPage: React.FC<{}> = () => {
  usePageTitle('Login - Sign In to Your Account');

  return (
    <div className={classes.loginPage}>
      <img src={treeImage} alt="Tree" className={classes.Tree} loading="lazy" />
      <LoginForm />
      <img src={frogImage} alt="Frog" className={classes.Frog} loading="lazy" />
    </div>
  );
}

export default LoginPage;

export const action: ActionFunction = async ({ request }) => {
  if (request.method !== 'POST') return null;

  const data = await request.formData();
  const authData = {
    email: data.get('email') as string | null,
    password: data.get('password') as string | null,
  };

  try {
    const response = await fetch(`${devbackendUrl}auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(authData)
    });

    if (response.status === 401 || response.status === 403) {
      const message = await getResponseErrorMessage(response, 'The email or password is incorrect.');
      return createAuthActionError('Sign in failed', message);
    }

    if (!response.ok) {
      const message = await getResponseErrorMessage(response, 'We could not sign you in right now. Please try again.');
      return createAuthActionError('Sign in unavailable', message);
    }

    const resData = await response.json();
    const token = resData.access_token;

    if (!token) {
      return createAuthActionError('Sign in unavailable', 'The server response was missing a session token. Please try again.');
    }

    let authenticatedUserId: number | null = null;
    try {
      authenticatedUserId = jwtDecode<{ sub: number }>(token).sub ?? null;
    } catch {
      authenticatedUserId = null;
    }
    const activeContext = readActiveGameContext();
    const hopContext = readHopGameData();
    const hasStoredGameState = Boolean(
      activeContext
      || hopContext
      || localStorage.getItem('standaloneGameData')
      || localStorage.getItem('currentGameId'),
    );
    const ownedByAuthenticatedUser = authenticatedUserId !== null && (
      activeContext?.userId === authenticatedUserId
      || Number(hopContext?.userId) === authenticatedUserId
    );
    if (hasStoredGameState && !ownedByAuthenticatedUser) {
      clearAllGameContexts();
    }
    localStorage.setItem('token', token);

    // Link analytics visitor to user after successful login
    try {
      const decoded = jwtDecode<{ sub: number }>(token);
      if (decoded.sub) {
        analyticsApi.linkUser(decoded.sub);
      }
    } catch (err) {
      console.debug('[Analytics] Failed to link user on login:', err);
    }

    return redirect(getRedirectToFromRequest(request));
  } catch (error) {
    console.error('[Auth] Login request failed:', error);
    return getNetworkAuthError('login');
  }
}
